/**
 * Model Commands Registry
 *
 * Hierarchical ve API destekli model komutları
 * Dynamic loader ile LLM ve Generative model listesi
 */

import { Sparkles, MessageSquare, Image } from "lucide-react";
import { apiClient } from "@/api/config/api-client";
import { API_ENDPOINTS } from "@/api/config/api-endpoints";
import type { SlashCommand } from "../types/command-types";
import type { LLMModelsResponse, LLMModelDescription } from "@/api/models/api-types";
import i18n from "@/lib/i18n";
import { modelsPaths } from "@/modules";

/**
 * Helper: Model description'ı string'e çevir
 */
const getDescriptionString = (description: LLMModelDescription | string | undefined): string | undefined => {
  if (!description) return undefined;
  if (typeof description === 'string') return description;
  
  // LLMModelDescription object ise, mevcut dildeki açıklamayı al
  const currentLang = i18n.language as keyof LLMModelDescription;
  return description[currentLang] || description.en || description.tr || undefined;
};

/**
 * Fetch LLM Models
 * API'den model listesini çeker
 */
const fetchLLMModels = async (
  provider_type: "llm" | "generative",
  searchTerm?: string,
  page: number = 1
): Promise<LLMModelsResponse> => {
  const searchParams = new URLSearchParams({
    page: String(page),
    limit: "10", // Slash command için daha az item göster
    provider_type,
  });

  if (searchTerm?.trim()) {
    searchParams.append("search", searchTerm.trim());
  }

  const response = await apiClient.get<LLMModelsResponse>(
    `${API_ENDPOINTS.getAllLLMModels}?${searchParams.toString()}`
  );

  return response;
};

/**
 * Model Commands
 *
 * Parent command: /model
 * Children: /model/llm, /model/generative
 * Each child has dynamic loader for API-based model list
 */
export const modelCommands: SlashCommand[] = [
  {
    id: "model-parent",
    trigger: "/model",
    label: "slashCommands.model",
    description: "slashCommands.modelDesc",
    icon: <Sparkles className="h-5 w-5" />,
    shortcut: "/m",
    type: "parent",
    children: [
      {
        id: "model-llm",
        trigger: "/model/llm",
        label: "slashCommands.llmModels",
        description: "slashCommands.llmDesc",
        icon: <MessageSquare className="h-4 w-4" />,
        type: "dynamic",
        dynamicLoader: async (query, page = 1) => {
          try {
            const response = await fetchLLMModels("llm", query, page);

            // API response'dan command'lara dönüştür
            return response.data.result.map((model) => ({
              id: `llm-${model._id}`,
              trigger: `/model/llm/${model.slug || model._id}`,
              label: model.name,
              description: getDescriptionString(model.description),
              icon: <MessageSquare className="h-4 w-4" />,
              type: "action" as const,
              action: async () => {
                // Chat context'inde ise chatId parametresini geç
                const chatId = window.location.pathname.startsWith("/chat/")
                  ? window.location.pathname.split("/")[2]
                  : null;

                const chatIdParam = chatId ? `/${chatId}` : "";

                // Model detay sayfasına git
                window.location.href = `${modelsPaths.wizard(model._id)}${chatIdParam}`;
              },
            }));
          } catch (error) {
            return [];
          }
        },
      },
      {
        id: "model-generative",
        trigger: "/model/generative",
        label: "slashCommands.generativeModels",
        description: "slashCommands.generativeDesc",
        icon: <Image className="h-4 w-4" />,
        type: "dynamic",
        dynamicLoader: async (query, page = 1) => {
          try {
            const response = await fetchLLMModels("generative", query, page);

            // API response'dan command'lara dönüştür
            return response.data.result.map((model) => ({
              id: `gen-${model._id}`,
              trigger: `/model/generative/${model.slug || model._id}`,
              label: model.name,
              description: getDescriptionString(model.description),
              icon: <Image className="h-4 w-4" />,
              type: "action" as const,
              action: async () => {
                // Chat context'inde ise chatId parametresini geç
                const chatId = window.location.pathname.startsWith("/chat/")
                  ? window.location.pathname.split("/")[2]
                  : null;

                const chatIdParam = chatId ? `/${chatId}` : "";

                // Model detay sayfasına git
                window.location.href = `${modelsPaths.wizard(model._id)}${chatIdParam}`;
              },
            }));
          } catch (error) {
            return [];
          }
        },
      },
    ],
  },
];
